/**
 * <s-product-image-zoom> — PDP image zoom preloader and previous/next navigation.
 *
 * Gallery buttons still open native dialogs declaratively via commandfor/show-modal.
 * This component warms the selected zoom image on hover/focus so the
 * fullscreen dialog has the high-resolution image ready when opened, and steps
 * between open zoom dialogs in gallery order (arrow buttons, arrow keys, swipe).
 *
 * Required markup:
 *   [data-product-gallery] [data-zoom-preload][commandfor] — gallery trigger buttons; commandfor points to a dialog id.
 *   dialog img[data-zoom-image]     — high-resolution image inside the target dialog.
 *   button[commandfor=<this id>][command="--zoom-prev" | "--zoom-next"] — arrows inside each dialog.
 *
 * Sets [data-single-image] on itself when the gallery shows fewer than two photos (arrows hide).
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);

    // Previous/next arrows in the zoom dialogs target this element with custom commands.
    this.addEventListener('command', (event) => {
      if (event.command === '--zoom-prev') this.#step(-1);
      else if (event.command === '--zoom-next') this.#step(1);
    }, opts);

    this.addEventListener('keydown', (event) => {
      if (!this.#openDialog()) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.#step(document.dir === 'rtl' ? 1 : -1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.#step(document.dir === 'rtl' ? -1 : 1);
      }
    }, opts);

    let swipeStartX = null;
    this.addEventListener('pointerdown', (event) => {
      swipeStartX = this.#openDialog() && event.pointerType !== 'mouse' ? event.clientX : null;
    }, opts);
    this.addEventListener('pointerup', (event) => {
      if (swipeStartX === null) return;
      const distance = event.clientX - swipeStartX;
      swipeStartX = null;
      if (Math.abs(distance) < 50) return;
      const forward = distance < 0 ? 1 : -1;
      this.#step(document.dir === 'rtl' ? -forward : forward);
    }, opts);
  }

  /** Gallery buttons in their current order (the gallery re-renders on colour change). */
  #triggers() {
    const triggers = [...this.querySelectorAll('[data-product-gallery] [data-zoom-preload][commandfor]')];
    this.toggleAttribute('data-single-image', triggers.length < 2);
    return triggers;
  }

  #openDialog() {
    return this.querySelector('dialog[open]');
  }

  #step(direction) {
    const current = this.#openDialog();
    if (!current) return;
    const triggers = this.#triggers();
    if (triggers.length < 2) return;

    const index = triggers.findIndex((trigger) => trigger.getAttribute('commandfor') === current.id);
    const nextIndex = index === -1 ? 0 : (index + direction + triggers.length) % triggers.length;
    const nextTrigger = triggers[nextIndex];
    const nextDialog = document.getElementById(nextTrigger.getAttribute('commandfor'));
    if (!(nextDialog instanceof HTMLDialogElement) || nextDialog === current) return;

    this.#preload(nextTrigger);
    current.close();
    // Focus the gallery photo before opening so closing the zoom later returns focus to the photo being viewed.
    nextTrigger.focus({ preventScroll: true });
    nextDialog.showModal();
    this.#preload(triggers[(nextIndex + direction + triggers.length) % triggers.length]);
  }

  disconnectedCallback() {
    this.#ac?.abort();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#triggers();
    this.#preload(source);
  };

  #preload(source) {
    const dialogId = source.getAttribute('commandfor');
    if (!dialogId || this.#preloaded.has(dialogId)) return;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialogId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
